import { AxiosError } from 'axios';
import { AuthContext } from 'context/authContext/AuthContext';
import { WSO2AuthLocalStorageNames } from 'helpers/enums/WSO2AuthLocalStorageNames';
import { AssessorRoutePath, MainRoutePath } from 'library/Routes';
import { UserManager, WebStorageStateStore } from 'oidc-client';
import { useContext, useLayoutEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { authActionsLoginByToken } from 'store/modules/AuthActions';
import UserRoleUnauthorizedScreen from 'pages/Errors/UserRoleUnauthorizedScreen/UserRoleUnauthorizedScreen';
import { getApis } from 'library/Apis';
import { getParsedJWTAssessorAccessToken } from 'helpers/utils/JWTUtils';

export default function AssessorArea({ children }: any) {
  const [loginError, setLoginError] = useState<AxiosError | null>();
  const history = useHistory();
  const {
    login,
    logout,
    currentEnv,
    manager,
    user,
    setUser,
    startInterceptor,
    setLoading,
  } = useContext(AuthContext);
  const dispatch = useDispatch();

  useLayoutEffect(() => {
    if (typeof login === 'function' && currentEnv) {
      login(
        new UserManager({
          ...currentEnv.authAssessor,
          automaticSilentRenew: true,
          userStore: new WebStorageStateStore({ store: window.sessionStorage }),
        })
      );
    }
  }, [currentEnv, login]);

  useLayoutEffect(() => {
    if (manager && history) {
      setLoading!('Carregando as informações do usuário');
      manager
        .getUser()
        .then((user) => {
          if (user) {
            setUser!(user);
          } else {
            throw new Error('Usuário não encontrado!');
          }
        })
        .catch(() => {
          manager
            .signinRedirectCallback()
            .then((usr) => {
              const hasCode = window.location.href.match(/code=([^&]*)/);
              if (hasCode) {
                const redirectUrl = localStorage.getItem(
                  WSO2AuthLocalStorageNames.RedirectUri
                );
                history.replace(redirectUrl || AssessorRoutePath);
              }

              setUser!(usr);
            })
            .catch(() => {
              setLoading!(false);
              localStorage.setItem(
                WSO2AuthLocalStorageNames.RedirectUri,
                window.location.pathname
              );
              manager?.signinRedirect();
            });
        });
      manager.events.addSilentRenewError(() => {
        logout!()!?.finally(() => {
          sessionStorage.removeItem('filter-assessor');
          history!?.push(MainRoutePath);
        });
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [manager]);

  useLayoutEffect(() => {
    startInterceptor!(() => {
      dispatch(
        authActionsLoginByToken(
          user!.access_token,
          () => {
            setLoading!(false);
          },
          (error) => {
            setLoginError(error);
            setLoading!(false);
          }
        )
      );
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const token: any = JSON.parse(
    localStorage.getItem(
      `oidc.user:https://wso2identity.hom.sicredi.net/oauth2/authorize:${
        getApis().authAssessor.client_id
      }`
    ) as any
  );

  const validationToken = useMemo(() => {
    if (token!?.access_token! === user!?.access_token!) {
      return true;
    }

    return false;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, loginError, children, logout]);

  const content = useMemo(() => {
    if (!user) {
      return null;
    } else if (loginError) {
      if (
        loginError.response?.status === 401 &&
        getParsedJWTAssessorAccessToken(token!?.access_token!)!?.exp! * 1000 <
          new Date().getTime() &&
        validationToken
      ) {
        localStorage.removeItem('filter-assessor');
        manager?.clearStaleState();
        manager
          ?.signoutRedirect({ extraQueryParams: { state: 'state_1' } })
          .finally(() => {
            window.location.href = '/assembleias';
          });
      }

      return <UserRoleUnauthorizedScreen toLogout={logout!} />;
    }

    return children;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, loginError, children, logout]);

  return content;
}
